import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useState } from 'react';
import { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';

import { AssetAllocation } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';

interface UseAssetAllocationManagerProps<T extends FieldValues> {
    form: UseFormReturn<T>;
    assetAllocations: AssetAllocation[];
    /** Field path to write updates to — defaults to the top-level 'assetAllocations' field. */
    fieldName?: Path<T>;
}

export const useAssetAllocationManager = <T extends FieldValues>({
    form,
    assetAllocations,
    fieldName = 'assetAllocations' as Path<T>,
}: UseAssetAllocationManagerProps<T>) => {
    const setAssetAllocations = (updated: AssetAllocation[]) => {
        form.setValue(fieldName, updated as PathValue<T, Path<T>>);
    };

    const [showAddAsset, setShowAddAsset] = useState(false);
    const [addingReplacementForAsset, setAddingReplacementForAsset] = useState<string | null>(null);

    const handleAddAsset = (asset: AssetInfo) => {
        if (!asset.scId) {
            toast.error(`${asset.symbol ?? asset.sId} has no share class id and cannot be added`);
            return;
        }
        const totalWeight = assetAllocations.reduce((sum, allocationAsset) => {
            const weight = parseFloat(allocationAsset.weight || '0');
            return sum + weight;
        }, 0);
        const remainingWeight = Math.max(0, 100 - totalWeight);
        const roundedRemainingWeight = Math.round(remainingWeight * 100) / 100;

        const newAsset: AssetAllocation = {
            securityId: asset.sId,
            shareClassId: asset.scId,
            weight: roundedRemainingWeight.toString(),
            replacementAssets: [],
        };
        const updatedAssets = [...assetAllocations, newAsset];
        setAssetAllocations(updatedAssets);
        setShowAddAsset(false);
    };

    const removeAsset = (securityId: string) => {
        setAssetAllocations(assetAllocations.filter((a) => a.securityId !== securityId));
    };

    const handleUpdateWeight = (securityId: string, weight: string) => {
        const updatedAssets = assetAllocations.map((a) => (a.securityId === securityId ? { ...a, weight } : a));
        setAssetAllocations(updatedAssets);
    };

    const handleAddReplacement = (asset: AssetInfo) => {
        const shareClassId = asset.scId;
        if (!shareClassId) {
            toast.error(`${asset.symbol ?? asset.sId} has no share class id and cannot be added`);
            return;
        }
        const updatedAssets = assetAllocations.map((allocation) => {
            if (allocation.securityId === addingReplacementForAsset) {
                return {
                    ...allocation,
                    replacementAssets: [
                        ...allocation.replacementAssets,
                        {
                            securityId: asset.sId,
                            shareClassId,
                        },
                    ],
                };
            }
            return allocation;
        });

        setAssetAllocations(updatedAssets);
        setAddingReplacementForAsset(null);
    };

    const removeReplacement = (assetSecurityId: string, replacementSecurityId: string) => {
        const updatedAssets = assetAllocations.map((asset) => {
            if (asset.securityId === assetSecurityId) {
                return {
                    ...asset,
                    replacementAssets: asset.replacementAssets.filter((r) => r.securityId !== replacementSecurityId),
                };
            }
            return asset;
        });
        setAssetAllocations(updatedAssets);
    };

    const evenSplit = () => {
        const n = assetAllocations.length;
        if (n === 0) return;
        const each = Math.floor(100 / n);
        const updatedAssets = assetAllocations.map((asset, index) => ({
            ...asset,
            weight: (index === 0 ? 100 - each * (n - 1) : each).toString(),
        }));
        setAssetAllocations(updatedAssets);
    };

    const handleDragEndReplacement = (event: DragEndEvent, assetSecurityId: string) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const updatedAssets = assetAllocations.map((asset) => {
            if (asset.securityId === assetSecurityId) {
                const oldIndex = asset.replacementAssets.findIndex((r) => r.securityId === active.id);
                const newIndex = asset.replacementAssets.findIndex((r) => r.securityId === over.id);

                return {
                    ...asset,
                    replacementAssets: arrayMove(asset.replacementAssets, oldIndex, newIndex),
                };
            }
            return asset;
        });

        setAssetAllocations(updatedAssets);
    };

    return {
        showAddAsset,
        setShowAddAsset,
        addingReplacementForAsset,
        setAddingReplacementForAsset,
        handleAddAsset,
        removeAsset,
        handleUpdateWeight,
        handleAddReplacement,
        removeReplacement,
        evenSplit,
        handleDragEndReplacement,
    };
};
